// ─── COMPLEX ORDERS ───────────────────────────────────────────────────────────
// Multi-leg packages found in single-name option prints after the close. The
// detection, the floor, the labels and the highlight rule live in the backend
// (app/services/complex_orders.py); this view only reads
//   GET /api/options/complex/tickers   per-ticker summary (the landing view)
//   GET /api/options/complex           packages, one line per structure
//   GET /api/options/complex/group     every repeat of one structure
//   GET /api/options/complex/{id}      one package's legs
//
// ⚠⚠ UNAVAILABLE MEANS NOTHING ELSE. A failed or "available: false" answer shows
// the one line UNAVAILABLE_TEXT — no filters, no note, no rows kept from before.
// ⚠ Every label comes from the server and reads "Consistent with …" (or "Complex
// package, N legs"), with the rule it matched beneath. Nothing here says who bought
// or sold, or which way a trade leans: the feed does not carry the side of each leg
// (see NOTE_TEXT). The call/put mix names which contracts the legs are, nothing more.
// ⚠ No price, premium or venue is shown; a missing value is left blank, never 0.
// Minimum new contracts is null when any leg's open interest is unknown — blank.
// ⚠ No top-N cutoff: both lists page through every match with the server's cursor
// ("Load more"), and the count says how many match.
import { useState, useEffect, useRef, useCallback } from "react";
import { C, font, backendFetch } from "./core";
import { SearchBox, TICKER_QUERY_RE, SEARCH_DEBOUNCE_MS } from "./components/SearchBox";

export const UNAVAILABLE_TEXT = "Complex order data unavailable";
export const NOTE_TEXT = "These are option legs that traded together as one order. The data does not say which "
  + "side bought or sold each leg, or whether the order opened or closed a position, so each package is "
  + "described only by its structure.";
export const NEW_CONTRACTS_TEXT = "Minimum new contracts: for each leg, the contracts beyond that leg's open "
  + "interest on the date shown, added up. Blank when a leg's open interest is not known.";
export const STORE_MIN = 50;            // the backend's floor (STORE_MIN_CONTRACTS)
export const DISPLAY_MIN = 250;         // the page's default (DISPLAY_MIN_CONTRACTS)
export const MIN_CONTRACT_STEPS = [50, 100, 250, 500, 1000];
export const RELATIVE_STEPS = [null, 0.1, 0.25, 0.5, 1];
export const SORTS = [
  { key: "new_contracts", label: "Minimum new contracts" },
  { key: "contracts", label: "Contracts, all legs" },
  { key: "relative_size", label: "Size vs open interest" },
  { key: "volume_share", label: "Share of day volume" },
  { key: "time", label: "Time" },
];
export const DEFAULT_SORT = "new_contracts";
export const PAGE_SIZE = 50;
export const MIX_WORDS = { calls: "Calls only", puts: "Puts only", calls_and_puts: "Calls and puts" };

const fin = (v) => typeof v === "number" && Number.isFinite(v);
const num = (v) => (fin(v) ? v : null);
const int = (v) => (Number.isInteger(v) ? v : null);

// ── data ─────────────────────────────────────────────────────────────────────
function repeatOf(r) {
  if (!r || !Number.isInteger(r.count) || r.count < 1) return null;
  return { count: r.count, contracts: int(r.contracts), first_ts: typeof r.first_ts === "string" ? r.first_ts : null,
    last_ts: typeof r.last_ts === "string" ? r.last_ts : null, highlight: r.highlight === true };
}
function packageOf(it) {
  if (!it || !Number.isInteger(it.id) || typeof it.underlying !== "string" || typeof it.label !== "string"
    || !fin(it.contracts) || it.contracts < STORE_MIN) return null;
  return { ...it, oi_ratio: num(it.oi_ratio), oi_ratio_min_leg: num(it.oi_ratio_min_leg),
    day_volume_share: num(it.day_volume_share), new_contracts: int(it.new_contracts),
    package_size: int(it.package_size), exchanges: int(it.exchanges),
    mix_label: typeof it.mix_label === "string" ? it.mix_label : "", highlight: it.highlight === true,
    oi_status: typeof it.oi_status === "string" && it.oi_status ? it.oi_status : null,
    repeat: repeatOf(it.repeat) };
}
function meta(body) {
  return {
    date: typeof body.date === "string" ? body.date : null,
    dates: Array.isArray(body.dates) ? body.dates.filter((d) => typeof d === "string") : [],
    shapes: Array.isArray(body.shapes) ? body.shapes.filter((s) => s && s.key && s.name) : [],
    mixes: Array.isArray(body.mixes) ? body.mixes.filter((s) => s && MIX_WORDS[s.key]) : [],
    next: typeof body.next_cursor === "string" ? body.next_cursor : null,
  };
}
// A usable package list answer, or null. A row missing its id, underlying, label or
// a contract count at or above the floor is dropped whole.
export function sanitize(body) {
  if (!body || body.available !== true || !Array.isArray(body.items)) return null;
  const items = body.items.map(packageOf).filter(Boolean);
  return { ...meta(body), total: fin(body.total) ? body.total : items.length,
    packages: fin(body.packages) ? body.packages : null, items };
}
export function sanitizeTickers(body) {
  if (!body || body.available !== true || !Array.isArray(body.items)) return null;
  const items = body.items.filter((t) => t && typeof t.underlying === "string" && fin(t.contracts)
    && Number.isInteger(t.packages) && t.packages > 0)
    .map((t) => ({ underlying: t.underlying, packages: t.packages, contracts: t.contracts, largest: int(t.largest),
      call_contracts: int(t.call_contracts), put_contracts: int(t.put_contracts), highlighted: int(t.highlighted) }));
  return { ...meta(body), total: fin(body.total) ? body.total : items.length, items };
}

function filterParams(f) {
  const p = new URLSearchParams();
  if (f.date) p.set("date", f.date);
  if (f.shape) p.set("shape", f.shape);
  if (f.mix && MIX_WORDS[f.mix]) p.set("mix", f.mix);
  p.set("min_contracts", String(Math.max(STORE_MIN, f.minContracts || DISPLAY_MIN)));
  if (fin(f.minRelative)) p.set("min_relative", String(f.minRelative));
  return p;
}
function searchParam(p, f) {
  const q = String(f.query || "").trim();
  if (q && TICKER_QUERY_RE.test(q)) p.set("q", q.toUpperCase());
}
export function buildQuery(f, cursor = null) {
  const p = filterParams(f);
  if (f.ticker) p.set("underlying", f.ticker);
  else searchParam(p, f);
  p.set("sort", f.sort || DEFAULT_SORT);
  p.set("limit", String(PAGE_SIZE));
  if (cursor) p.set("cursor", cursor);
  return p.toString();
}
export function buildTickerQuery(f, cursor = null) {
  const p = filterParams(f);
  searchParam(p, f);
  p.set("limit", String(PAGE_SIZE));
  if (cursor) p.set("cursor", cursor);
  return p.toString();
}
export function buildGroupQuery(f, it) {
  const p = filterParams(f);
  p.set("underlying", it.underlying);
  p.set("structure_key", it.structure_key);
  return p.toString();
}

async function getJson(path) {
  const res = await backendFetch(null, path);
  if (!res || !res.ok) return null;
  return res.json();
}
export async function fetchList(f, cursor = null) {
  return sanitize(await getJson(`/api/options/complex?${buildQuery(f, cursor)}`));
}
export async function fetchTickers(f, cursor = null) {
  return sanitizeTickers(await getJson(`/api/options/complex/tickers?${buildTickerQuery(f, cursor)}`));
}
export async function fetchGroup(f, it) {
  const b = await getJson(`/api/options/complex/group?${buildGroupQuery(f, it)}`);
  return b && b.available === true && Array.isArray(b.items) ? b.items.map(packageOf).filter(Boolean) : null;
}
export async function fetchDetail(id) {
  const b = await getJson(`/api/options/complex/${encodeURIComponent(id)}`);
  return b && b.available === true && Array.isArray(b.legs) ? b : null;
}

// ── formatting ───────────────────────────────────────────────────────────────
const nf = new Intl.NumberFormat("en-US");
export function fmtTime(iso) {
  if (typeof iso !== "string") return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", { timeZone: "America/New_York", hour12: false, hour: "2-digit",
    minute: "2-digit", second: "2-digit" });
}
export function fmtExpiry(a, b) {
  if (typeof a !== "string") return "";
  return b && b !== a ? `${a} to ${b}` : a;
}
export function fmtRelative(it) {
  const parts = [];
  if (fin(it.oi_ratio)) parts.push(`${it.oi_ratio >= 10 ? it.oi_ratio.toFixed(0) : it.oi_ratio.toFixed(2)}× open interest`);
  if (fin(it.day_volume_share)) parts.push(`${Math.round(it.day_volume_share * 100)}% of the legs' day volume`);
  return parts.join(" · ");
}
// The server says why open interest cannot be used ("open interest unavailable for
// this date": it is stamped on or after the trade date and already includes the day).
// Shown in place of the date; the numbers it would feed stay blank, like missing OI.
export function fmtOiDate(it, long = false) {
  if (it.oi_status) return it.oi_status.charAt(0).toUpperCase() + it.oi_status.slice(1);
  if (!it.oi_date) return "";
  return long ? `Open interest as of ${it.oi_date}` : `OI as of ${it.oi_date}`;
}
export function fmtHeadline(it) {
  const s = `${nf.format(it.contracts)} contracts, all legs`;
  return fin(it.package_size) ? `${s} · ${nf.format(it.package_size)} per leg` : s;
}
export function fmtRepeat(r) {
  if (!r || r.count < 2) return "";
  const span = r.first_ts && r.last_ts ? ` · ${fmtTime(r.first_ts)} to ${fmtTime(r.last_ts)} ET` : "";
  const tot = fin(r.contracts) ? ` · ${nf.format(r.contracts)} contracts in all` : "";
  return `Repeated ${r.count} times${tot}${span}`;
}

// ── view ─────────────────────────────────────────────────────────────────────
const sel = { background: C.panel, color: C.text, border: `1px solid ${C.border}`, fontFamily: font, fontSize: 12,
  padding: "6px 8px", minHeight: 36, borderRadius: 3 };
const lbl = { color: C.textMid, fontSize: 10, letterSpacing: "0.08em", display: "block", marginBottom: 3 };

export function Filters({ f, setF, data, view }) {
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <div data-filters style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
      <label><span style={lbl}>DATE</span>
        <select aria-label="Date" value={f.date || ""} onChange={set("date")} style={sel}>
          {(data?.dates || []).map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </label>
      <label><span style={lbl}>STRUCTURE</span>
        <select aria-label="Structure" value={f.shape || ""} onChange={set("shape")} style={sel}>
          <option value="">All structures</option>
          {(data?.shapes || []).map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
        </select>
      </label>
      <label><span style={lbl}>CALLS / PUTS</span>
        <select aria-label="Calls and puts" value={f.mix || ""} onChange={set("mix")} style={sel}>
          <option value="">All</option>
          {(data?.mixes || []).map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
        </select>
      </label>
      <label><span style={lbl}>MIN CONTRACTS</span>
        <select aria-label="Minimum contracts" value={f.minContracts}
          onChange={(e) => setF((x) => ({ ...x, minContracts: Number(e.target.value) }))} style={sel}>
          {MIN_CONTRACT_STEPS.map((n) => <option key={n} value={n}>{nf.format(n)}+</option>)}
        </select>
      </label>
      <label><span style={lbl}>SIZE VS OPEN INTEREST</span>
        <select aria-label="Minimum size versus open interest" value={fin(f.minRelative) ? String(f.minRelative) : ""}
          onChange={(e) => setF((x) => ({ ...x, minRelative: e.target.value === "" ? null : Number(e.target.value) }))}
          style={sel}>
          {RELATIVE_STEPS.map((v) => <option key={String(v)} value={v === null ? "" : String(v)}>
            {v === null ? "Any" : `${v}× or more`}</option>)}
        </select>
      </label>
      {view === "packages" && (
        <label><span style={lbl}>SORT</span>
          <select aria-label="Sort" value={f.sort} onChange={set("sort")} style={sel}>
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
      )}
    </div>
  );
}

export function Row({ it, open, onToggle, member = false }) {
  const rel = fmtRelative(it);
  const marked = it.highlight || (!member && it.repeat?.highlight);
  return (
    <div data-row={it.id} data-highlight={marked ? "1" : "0"}
      style={{ borderBottom: `1px solid ${C.border}`, borderLeft: `3px solid ${marked ? C.amber : "transparent"}`,
        background: open ? C.panelAlt : "transparent" }}>
      <button onClick={() => onToggle(it.id)} aria-expanded={open}
        style={{ all: "unset", cursor: "pointer", display: "flex", flexWrap: "wrap", gap: "4px 14px", width: "100%",
          boxSizing: "border-box", padding: "8px 10px", fontFamily: font, fontSize: 12, color: C.text }}>
        <span data-f="ticker" style={{ color: C.amber, fontWeight: 700, minWidth: 56 }}>{it.underlying}</span>
        <span data-f="time" style={{ color: C.textMid, minWidth: 64 }}>{fmtTime(it.first_ts)}</span>
        <span data-f="label" style={{ flex: "1 1 240px" }}>{it.label}</span>
        <span data-f="mix" style={{ color: C.textMid, minWidth: 90 }}>{it.mix_label}</span>
        <span data-f="contracts" style={{ minWidth: 160 }}>{fmtHeadline(it)}</span>
        <span data-f="new" style={{ color: it.highlight ? C.amber : C.textMid, minWidth: 130 }}>
          {fin(it.new_contracts) ? `${nf.format(it.new_contracts)} min. new` : ""}</span>
        <span data-f="expiry" style={{ color: C.textMid, minWidth: 150 }}>{fmtExpiry(it.min_expiry, it.max_expiry)}</span>
        <span data-f="oidate" style={{ color: C.textMid, minWidth: 110 }}>{fmtOiDate(it)}</span>
        <span data-f="relative" style={{ color: C.textMid, flex: "1 1 220px" }}>{rel}</span>
      </button>
    </div>
  );
}

export function Detail({ d }) {
  if (!d) return null;
  const cell = { padding: "4px 8px", borderBottom: `1px solid ${C.border}`, textAlign: "right" };
  return (
    <div data-detail={d.id} style={{ padding: "8px 10px 12px 13px", background: C.panelAlt, fontSize: 12 }}>
      <div data-f="label" style={{ color: C.text }}>{d.label}</div>
      <div data-f="rule" style={{ color: C.textMid, marginBottom: 6 }}>Matched: {d.rule}</div>
      <div data-f="size" style={{ color: C.textMid, marginBottom: 6 }}>
        {nf.format(d.contracts)} contracts across {d.legs_count} legs
        {fin(d.package_size) ? ` (${nf.format(d.package_size)} per leg)` : ""}
        {d.slices > 1 ? `, in ${d.slices} pieces between ${fmtTime(d.first_ts)} and ${fmtTime(d.last_ts)} ET` : `, at ${fmtTime(d.first_ts)} ET`}
        {fin(d.exchanges) && d.exchanges > 1 ? `, on ${d.exchanges} exchanges` : ""}
      </div>
      <div data-f="oidate" style={{ color: C.textMid, marginBottom: 6 }}>
        {fmtOiDate(d, true)}
        {fin(d.new_contracts) ? `${fmtOiDate(d, true) ? " · " : ""}${nf.format(d.new_contracts)} minimum new contracts` : ""}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table data-legs style={{ borderCollapse: "collapse", fontFamily: font, fontSize: 12, color: C.text }}>
          <thead><tr style={{ color: C.textMid }}>
            {["Contract", "Type", "Strike", "Expiry", "Contracts", "Open interest", "Day volume"].map((h) =>
              <th key={h} style={{ ...cell, textAlign: h === "Contract" || h === "Type" ? "left" : "right", fontWeight: 400 }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {d.legs.map((l) => (
              <tr key={l.contract} data-leg={l.contract}>
                <td style={{ ...cell, textAlign: "left" }}>{l.contract}</td>
                <td style={{ ...cell, textAlign: "left" }}>{l.type === "call" ? "Call" : l.type === "put" ? "Put" : ""}</td>
                <td style={cell}>{fin(l.strike) ? l.strike : ""}</td>
                <td style={cell}>{typeof l.expiry === "string" ? l.expiry : ""}</td>
                <td data-f="legsize" style={cell}>{fin(l.contracts) ? nf.format(l.contracts) : ""}</td>
                <td data-f="oi" style={cell}>{fin(l.open_interest) ? nf.format(l.open_interest) : ""}</td>
                <td style={cell}>{fin(l.day_volume) ? nf.format(l.day_volume) : ""}</td>
              </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function TickerTable({ items, onPick }) {
  const cell = { padding: "6px 8px", borderBottom: `1px solid ${C.border}`, textAlign: "right", whiteSpace: "nowrap" };
  const blank = (v) => (fin(v) ? nf.format(v) : "");
  return (
    <div style={{ overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 3 }}>
      <table data-tickers style={{ borderCollapse: "collapse", fontFamily: font, fontSize: 12, color: C.text, width: "100%" }}>
        <thead><tr style={{ color: C.textMid }}>
          {["Ticker", "Packages", "Contracts, all legs", "Largest", "Call contracts", "Put contracts", "Highlighted"].map((h) =>
            <th key={h} style={{ ...cell, textAlign: h === "Ticker" ? "left" : "right", fontWeight: 400 }}>{h}</th>)}
        </tr></thead>
        <tbody>
          {items.map((t) => (
            <tr key={t.underlying} data-ticker={t.underlying}>
              <td style={{ ...cell, textAlign: "left" }}>
                <button onClick={() => onPick(t.underlying)} style={{ all: "unset", cursor: "pointer", color: C.amber,
                  fontWeight: 700, minHeight: 28, display: "inline-flex", alignItems: "center" }}>{t.underlying}</button>
              </td>
              <td style={cell}>{blank(t.packages)}</td>
              <td data-f="contracts" style={cell}>{blank(t.contracts)}</td>
              <td style={cell}>{blank(t.largest)}</td>
              <td data-f="calls" style={cell}>{blank(t.call_contracts)}</td>
              <td data-f="puts" style={cell}>{blank(t.put_contracts)}</td>
              <td style={{ ...cell, color: t.highlighted ? C.amber : C.textMid }}>{blank(t.highlighted)}</td>
            </tr>))}
        </tbody>
      </table>
    </div>
  );
}

const tabBtn = (on) => ({ ...sel, cursor: "pointer", color: on ? C.amber : C.textMid,
  borderColor: on ? C.amber : C.border });

export function ComplexOrdersTab() {
  const [f, setF] = useState({ query: "", date: null, shape: "", mix: "", minContracts: DISPLAY_MIN,
    minRelative: null, sort: DEFAULT_SORT, ticker: null });
  const [view, setView] = useState("tickers");
  const [st, setSt] = useState({ status: "loading", data: null, items: [] });
  const [more, setMore] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [details, setDetails] = useState({});
  const [groups, setGroups] = useState({});        // representative id → members | "loading" | "unavailable"
  const seq = useRef(0);
  const mode = f.ticker ? "packages" : view;

  const load = useCallback((ff, cursor) => (mode === "tickers" ? fetchTickers(ff, cursor) : fetchList(ff, cursor)),
    [mode]);

  // One request per filter or view change (search debounced); a newer request wins.
  useEffect(() => {
    const mine = ++seq.current;
    // Rows for the old filters are never shown under the new ones.
    setSt((s0) => ({ status: "loading", data: s0.data, items: [] }));
    setOpenId(null);
    setGroups({});
    const q = String(f.query || "").trim();
    const wait = q && !f.ticker ? SEARCH_DEBOUNCE_MS : 0;
    const t = setTimeout(async () => {
      let data = null;
      try { data = await load(f); } catch { data = null; }
      if (seq.current !== mine) return;
      if (!data) { setSt({ status: "unavailable", data: null, items: [] }); return; }
      setSt({ status: "ok", data, items: data.items, mode });
      if (!f.date && data.date) setF((x) => (x.date ? x : { ...x, date: data.date }));
    }, wait);
    return () => clearTimeout(t);
  }, [f, load, mode]);

  const loadMore = useCallback(async () => {
    if (!st.data?.next || more) return;
    setMore(true);
    const mine = seq.current;
    let data = null;
    try { data = await load(f, st.data.next); } catch { data = null; }
    setMore(false);
    if (seq.current !== mine) return;
    if (!data) { setSt({ status: "unavailable", data: null, items: [] }); return; }
    const keyOf = (x) => (mode === "tickers" ? x.underlying : x.id);
    setSt((s0) => ({ status: "ok", data: { ...s0.data, next: data.next, total: data.total }, mode: s0.mode,
      items: [...s0.items, ...data.items.filter((it) => !s0.items.some((x) => keyOf(x) === keyOf(it)))] }));
  }, [f, st, more, load, mode]);

  // key: which line is open (a member line is "m<id>", so a package that is both a
  // group's lead and one of its members never shows its legs twice).
  const toggle = useCallback(async (id, key = id) => {
    if (openId === key) { setOpenId(null); return; }
    setOpenId(key);
    if (details[id]) return;
    let d = null;
    try { d = await fetchDetail(id); } catch { d = null; }
    setDetails((x) => ({ ...x, [id]: d || "unavailable" }));
  }, [openId, details]);

  const toggleGroup = useCallback(async (it) => {
    if (groups[it.id]) { setGroups((g) => { const n = { ...g }; delete n[it.id]; return n; }); return; }
    setGroups((g) => ({ ...g, [it.id]: "loading" }));
    const mine = seq.current;
    let m = null;
    try { m = await fetchGroup(f, it); } catch { m = null; }
    if (seq.current !== mine) return;
    setGroups((g) => ({ ...g, [it.id]: m || "unavailable" }));
  }, [groups, f]);

  if (st.status === "unavailable") {
    return <div data-unavailable style={{ padding: 16, color: C.textMid, fontFamily: font, fontSize: 13 }}>{UNAVAILABLE_TEXT}</div>;
  }
  const data = st.data;
  const pick = (u) => setF((x) => ({ ...x, ticker: u }));
  const detailOf = (id, key = id) => (openId === key && (details[id] === "unavailable"
    ? <div data-detail-unavailable style={{ padding: "8px 13px", color: C.textMid, fontSize: 12 }}>Leg detail unavailable</div>
    : <Detail d={details[id]} />));
  return (
    <div style={{ padding: "12px 12px 24px", fontFamily: font, color: C.text, maxWidth: "100%", boxSizing: "border-box" }}>
      <div style={{ color: C.amber, fontWeight: 700, letterSpacing: "0.12em", fontSize: 13, marginBottom: 4 }}>COMPLEX ORDERS</div>
      <p data-note style={{ color: C.textMid, fontSize: 12, lineHeight: 1.5, margin: "0 0 4px", maxWidth: 820 }}>{NOTE_TEXT}</p>
      <p data-help style={{ color: C.textMid, fontSize: 11, lineHeight: 1.5, margin: "0 0 10px", maxWidth: 820 }}>{NEW_CONTRACTS_TEXT}</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
        {f.ticker ? (
          <button data-back onClick={() => setF((x) => ({ ...x, ticker: null }))} style={tabBtn(false)}>← All tickers</button>
        ) : (
          <>
            <button data-view="tickers" aria-pressed={view === "tickers"} onClick={() => setView("tickers")} style={tabBtn(view === "tickers")}>By ticker</button>
            <button data-view="packages" aria-pressed={view === "packages"} onClick={() => setView("packages")} style={tabBtn(view === "packages")}>All packages</button>
          </>
        )}
      </div>
      {f.ticker
        ? <div data-ticker-title style={{ color: C.amber, fontWeight: 700, margin: "4px 0 8px" }}>{f.ticker}</div>
        : <SearchBox value={f.query} onChange={(v) => setF((x) => ({ ...x, query: v }))} placeholder="Search by ticker..." />}
      <Filters f={f} setF={setF} data={data} view={mode} />
      {/* ⚠ st.mode !== mode: the view just switched and the effect has not cleared
          the other view's items yet — never render ticker rows as packages. */}
      {st.status === "loading" || !data || st.mode !== mode ? (
        <div style={{ color: C.textMid, fontSize: 12 }}>Loading…</div>
      ) : (
        <>
          <div data-count style={{ color: C.textMid, fontSize: 11, marginBottom: 6 }}>
            {mode === "tickers"
              ? `${nf.format(st.items.length)} of ${nf.format(data.total)} tickers`
              : `${nf.format(st.items.length)} of ${nf.format(data.total)} structures`
                + (fin(data.packages) ? ` (${nf.format(data.packages)} packages)` : "")}
            {data.date ? ` · ${data.date}` : ""}
          </div>
          {st.items.length === 0 ? (
            <div data-empty style={{ color: C.textMid, fontSize: 12, padding: "8px 0" }}>
              {mode === "tickers" ? "No tickers match these filters." : "No packages match these filters."}</div>
          ) : mode === "tickers" ? (
            <TickerTable items={st.items} onPick={pick} />
          ) : (
            <div data-list style={{ border: `1px solid ${C.border}`, borderRadius: 3 }}>
              {st.items.map((it) => (
                <div key={it.id}>
                  <Row it={it} open={openId === it.id} onToggle={toggle} />
                  {detailOf(it.id)}
                  {it.repeat && it.repeat.count > 1 && (
                    <button data-repeat={it.id} aria-expanded={!!groups[it.id]} onClick={() => toggleGroup(it)}
                      style={{ all: "unset", cursor: "pointer", display: "block", padding: "4px 13px 8px", fontSize: 11,
                        color: C.textMid, minHeight: 24, boxSizing: "border-box" }}>
                      {groups[it.id] ? "▾ " : "▸ "}{fmtRepeat(it.repeat)}
                    </button>
                  )}
                  {Array.isArray(groups[it.id]) && (
                    <div data-members={it.id} style={{ marginLeft: 13, borderLeft: `1px solid ${C.border}` }}>
                      {groups[it.id].map((m) => (
                        <div key={m.id}>
                          <Row it={m} member open={openId === "m" + m.id} onToggle={(id) => toggle(id, "m" + id)} />
                          {detailOf(m.id, "m" + m.id)}
                        </div>))}
                    </div>
                  )}
                  {groups[it.id] === "unavailable" && (
                    <div data-members-unavailable style={{ padding: "4px 13px 8px", color: C.textMid, fontSize: 11 }}>Repeats unavailable</div>
                  )}
                </div>))}
            </div>
          )}
          {data.next && (
            <button data-more onClick={loadMore} disabled={more}
              style={{ ...sel, marginTop: 10, cursor: "pointer", color: C.amber }}>
              {more ? "Loading…" : `Load more (${nf.format(data.total - st.items.length)} left)`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default ComplexOrdersTab;
